import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOCATIONS = ["1", "2-5", "6-10", "11+"];
const PRACTITIONERS = ["Solo", "2-5", "6-15", "16+"];
const PAIN_OPTIONS = [
  "Missed calls",
  "No-shows",
  "Low reactivation",
  "Manual front desk",
  "No exec reporting",
  "Multi-location chaos",
  "Website leads not booking",
];

/* EMR-specific demo form mounted inline on /integrations/:slug pages.
   POSTs to /api/leads with EMR fields tagged via `source` + `emr`.
   `compact` switches to a denser layout suitable for modals (no scroll). */
export const EmrDemoForm = ({ emr, page, compact = false }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    clinic_name: "",
    website_url: "",
    emr_url: "",
    locations: "",
    practitioners: "",
    pain_points: [],
    wants_exec_reporting: page.showExecReporting,
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const togglePain = (p) =>
    setForm((f) => ({
      ...f,
      pain_points: f.pain_points.includes(p)
        ? f.pain_points.filter((x) => x !== p)
        : [...f.pain_points, p],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          emr: emr.name,
          source: `${emr.slug}-page`,
        }),
      });
      if (!res.ok) throw new Error("submit");
      setOk(true);
    } catch (_e) {
      setErr("Couldn't submit — please try again, or email us.");
    } finally {
      setBusy(false);
    }
  };

  if (ok) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        data-testid={`emr-form-${emr.slug}-success`}
        className="rounded-3xl border border-emerald-500/30 bg-emerald-500/[0.06] p-8 text-center sm:p-12"
      >
        <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="h-7 w-7" />
        </span>
        <h3 className="mt-5 font-display text-2xl font-black tracking-tight sm:text-3xl">
          You&apos;re booked in.
        </h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
          We&apos;ll review your {emr.name} setup and reach out within one business day to schedule your personalised walk-through.
        </p>
      </motion.div>
    );
  }

  const gradient = `linear-gradient(135deg, ${page.hero.brandFromHex}, ${page.hero.brandToHex})`;

  return (
    <form
      onSubmit={submit}
      data-testid={`emr-form-${emr.slug}`}
      className={
        compact
          ? "rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6"
          : "rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8 lg:p-10"
      }
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <span
          className={`inline-flex flex-none items-center justify-center rounded-2xl text-white shadow-md ring-1 ring-inset ring-white/15 ${compact ? "h-10 w-10" : "h-12 w-12"}`}
          style={{ backgroundImage: gradient }}
        >
          <img
            src={emr.logo}
            alt={`${emr.name} logo`}
            className={`rounded-md bg-white object-contain p-0.5 ${compact ? "h-6 w-6" : "h-7 w-7"}`}
          />
        </span>
        <div className="flex-1">
          <p
            className="text-[10.5px] font-bold uppercase tracking-[0.22em]"
            style={{ color: page.hero.brandFromHex }}
          >
            Book your {emr.name} walk-through
          </p>
          <h3 className={`mt-1 font-display font-black tracking-tight ${compact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"}`}>
            {page.formTitle}
          </h3>
          {!compact && (
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground sm:text-sm">
              {page.formSub}
            </p>
          )}
        </div>
      </div>

      {/* Fields — 3-col on compact */}
      <div className={compact ? "mt-5 grid gap-3 sm:grid-cols-3" : "mt-7 grid gap-4 sm:grid-cols-2"}>
        <Field label="Your name *" testid="emr-name">
          <input required value={form.name} onChange={set("name")} type="text"
            placeholder="Dr. Sarah Chen" data-testid="emr-form-name"
            className={inputCls} />
        </Field>
        <Field label="Email *" testid="emr-email">
          <input required value={form.email} onChange={set("email")} type="email"
            placeholder="sarah@yourclinic.com" data-testid="emr-form-email"
            className={inputCls} />
        </Field>
        <Field label="Phone *" testid="emr-phone">
          <input required value={form.phone} onChange={set("phone")} type="tel"
            placeholder="+1 (555) 555-5555" data-testid="emr-form-phone"
            className={inputCls} />
        </Field>
        <Field label="Clinic name *" testid="emr-clinic">
          <input required value={form.clinic_name} onChange={set("clinic_name")} type="text"
            placeholder="Apex Physio" data-testid="emr-form-clinic"
            className={inputCls} />
        </Field>
        <Field label={`${page.formEmrLabel} *`} testid="emr-url">
          <input required value={form.emr_url} onChange={set("emr_url")} type="text"
            placeholder={page.formEmrPlaceholder} data-testid="emr-form-emr-url"
            className={inputCls} />
        </Field>
        <Field label="Clinic website" testid="emr-website">
          <input value={form.website_url} onChange={set("website_url")} type="text"
            placeholder="https://yourclinic.com" data-testid="emr-form-website"
            className={inputCls} />
        </Field>

        {/* Locations */}
        <Field label="Locations *" testid="emr-locations">
          <ChipRow
            options={LOCATIONS}
            value={form.locations}
            onChange={(v) => setForm((f) => ({ ...f, locations: v }))}
            testidPrefix="emr-form-locations"
            activeBg={gradient}
          />
        </Field>
        <Field label="Practitioners *" testid="emr-prac">
          <ChipRow
            options={PRACTITIONERS}
            value={form.practitioners}
            onChange={(v) => setForm((f) => ({ ...f, practitioners: v }))}
            testidPrefix="emr-form-practitioners"
            activeBg={gradient}
          />
        </Field>
      </div>

      {/* Pain points multi-select */}
      <div className={compact ? "mt-4" : "mt-6"}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          What are you trying to fix? <span className="font-normal normal-case text-muted-foreground/70">· pick any</span>
        </p>
        <div className={`flex flex-wrap ${compact ? "mt-2 gap-1.5" : "mt-2.5 gap-2"}`}>
          {PAIN_OPTIONS.map((p) => {
            const active = form.pain_points.includes(p);
            return (
              <button
                key={p}
                type="button"
                onClick={() => togglePain(p)}
                data-testid={`emr-form-pain-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                aria-pressed={active}
                style={active ? { backgroundImage: gradient, borderColor: "transparent" } : undefined}
                className={
                  active
                    ? `inline-flex items-center rounded-full text-white shadow-sm transition-all ${compact ? "gap-1 px-2.5 py-1 text-[11px] font-bold" : "gap-1.5 px-3 py-1.5 text-[12px] font-bold"}`
                    : `inline-flex items-center rounded-full border border-border bg-card text-foreground/85 transition-all hover:-translate-y-0.5 hover:border-foreground/40 ${compact ? "gap-1 px-2.5 py-1 text-[11px] font-semibold" : "gap-1.5 px-3 py-1.5 text-[12px] font-semibold"}`
                }
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      {/* Juvonno-only — exec reporting toggle */}
      {page.showExecReporting && (
        <label
          data-testid="emr-form-exec-reporting"
          className={`flex cursor-pointer items-start gap-3 rounded-2xl border border-border bg-secondary/30 transition-all hover:border-foreground/30 ${compact ? "mt-4 p-3" : "mt-6 p-4"}`}
        >
          <input
            type="checkbox"
            checked={form.wants_exec_reporting}
            onChange={(e) => setForm((f) => ({ ...f, wants_exec_reporting: e.target.checked }))}
            className="mt-0.5 h-4 w-4 flex-none rounded border-border"
          />
          <div className="flex-1">
            <p className={`font-bold tracking-tight ${compact ? "text-[12.5px]" : "text-[13.5px]"}`}>
              Show me the Executive Reporting Dashboard in the demo
            </p>
            <p className={`mt-0.5 leading-snug text-muted-foreground ${compact ? "text-[11px]" : "text-[12px]"}`}>
              Multi-location revenue · clinician utilisation · no-show rate · funnel — pulled live from your Juvonno account.
            </p>
          </div>
        </label>
      )}

      {/* Free-text (hidden in compact to fit viewport without scroll) */}
      {!compact && (
        <Field className="mt-6" label="Anything else we should know?" testid="emr-message">
          <textarea
            rows={3}
            value={form.message}
            onChange={set("message")}
            placeholder="Specific workflow, integration with another tool, anything we can prep for…"
            data-testid="emr-form-message"
            className={`${inputCls} resize-none py-2.5`}
          />
        </Field>
      )}

      {err && (
        <p className="mt-4 text-center text-xs font-medium text-rose-500" data-testid="emr-form-error">
          {err}
        </p>
      )}

      <div className={`flex items-start gap-2 rounded-lg border border-blue-500/25 bg-blue-500/[0.05] p-3 text-[11.5px] leading-snug text-foreground/85 ${compact ? "mt-4" : "mt-6"}`}>
        <span className="mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-300 text-[10px] font-bold">i</span>
        <p>
          <span className="font-bold">Pricing.</span>{" "}
          <span className="text-muted-foreground">
            Base starts at <span className="font-semibold text-foreground">$297 / $497 per location</span> (fixed). Any add-ons are quoted separately after the discovery call.
          </span>
        </p>
      </div>

      {/* Submit */}
      <div className={`flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between ${compact ? "mt-5 sm:gap-4" : "mt-7 sm:gap-6"}`}>
        <p className="whitespace-nowrap text-[11.5px] leading-snug text-muted-foreground">
          We&apos;ll review your {emr.name} setup before the call. No spam · disconnect anytime.
        </p>
        <button
          type="submit"
          disabled={busy}
          data-testid={`emr-form-${emr.slug}-submit`}
          style={{ backgroundImage: gradient }}
          className={`group inline-flex flex-none items-center justify-center gap-2 whitespace-nowrap rounded-xl px-7 font-bold text-white shadow-lg ring-1 ring-inset ring-white/15 transition-all hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60 ${compact ? "h-11 text-[12.5px]" : "h-12 text-[13px]"}`}
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              Request my {emr.name} walk-through
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

/* ---------- helpers ---------- */
const inputCls =
  "block w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none ring-ring transition-all focus:ring-2";

const Field = ({ label, children, className = "", testid }) => (
  <label className={className} data-testid={testid}>
    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const ChipRow = ({ options, value, onChange, testidPrefix, activeBg }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => {
      const active = value === o;
      return (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          aria-pressed={active}
          data-testid={`${testidPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          style={active ? { backgroundImage: activeBg, borderColor: "transparent" } : undefined}
          className={
            active
              ? "inline-flex items-center rounded-full px-3 py-1.5 text-[12px] font-bold text-white shadow-sm transition-all"
              : "inline-flex items-center rounded-full border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground/85 transition-all hover:-translate-y-0.5 hover:border-foreground/40"
          }
        >
          {o}
        </button>
      );
    })}
  </div>
);
